import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { confirmAlert } from 'react-confirm-alert';
import { useSelector } from 'react-redux';

import { releaseUrlData, reserveUrlData, updateUrlData } from '../../api/url_data';
import { applyEvaluation, correctEvaluation, getOpportunityLinks, sendEvaluationToHuman } from '../../api/url_data_agent_evaluations';
import { ADMIN_SCOPES } from '../../constants/admin_scope_constants';
import { hasScope } from '../../helpers/scope_helpers';
import { formatNumber } from '../../helpers/url_data_agent_evaluations_helpers';
import { SEND_BACK_REASON_OPTIONS, getSendBackBody } from '../../helpers/url_data_agent_live_matches_helpers';
import {
  OPEN_LINK_STATES,
  OPPORTUNITY_LINK_STATE_OPTIONS,
  SKIP_REASON_OPTIONS,
  getLinkCountKey,
  getOpportunityLinkCorrectionRoute,
  getOpportunityLinkPickerRow,
  getOpportunityLinkUpdate,
  getOpportunityLinkUrls,
  getOpportunityWindow,
  pickRevertsAgentApply,
  replaceOpportunityLink
} from '../../helpers/url_data_agent_opportunities_helpers';
import { useSearchParamsState } from '../../hooks/useSearchParamsState';
import ConfirmPrompt from '../General/ConfirmPrompt';
import LiveMatchProductPicker from './LiveMatchProductPicker';
import LiveMatchRowSkeleton from './LiveMatchRowSkeleton';
import OpportunityLinkRow from './OpportunityLinkRow';
import SegmentedControl from './SegmentedControl';

import './SilentEvalOpportunityLinks.scss';

const PAGE_SIZE = 100;
const MAX_ROWS = 500;
const URL_DEFAULTS = { linkState: 'open' };

// One opportunity's participant links: where matching stands on each, and the buttons to finish or correct it here.
const SilentEvalOpportunityLinks = ({ Opportunity_id, active = true, onBack }) => {
  const [urlState, patchUrl] = useSearchParamsState(URL_DEFAULTS);
  const currentUser = useSelector(state => state.users.currentUser);
  const canMatchUrls = hasScope(currentUser?.scopes || [], ADMIN_SCOPES.MATCH_PRODUCT);
  const linkState = OPPORTUNITY_LINK_STATE_OPTIONS.some(option => option.value === urlState.linkState) ? urlState.linkState : URL_DEFAULTS.linkState;
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [busyStems, setBusyStems] = useState(() => new Set());
  const [pickerLink, setPickerLink] = useState(null);
  const pickerRow = useMemo(() => pickerLink && getOpportunityLinkPickerRow(pickerLink), [pickerLink]);
  const loadedCountRef = useRef(0);
  const linkRereadsRef = useRef([]);
  const current = report?.opportunity.id === Opportunity_id && report.linkState === linkState ? report : null;
  const rows = current?.rows || [];
  loadedCountRef.current = rows.length;

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const withRereads = beginPageRead();
    setIsFetching(true);
    setError(null);
    getOpportunityLinks(Opportunity_id, { state: linkState, limit: Math.min(Math.max(loadedCountRef.current, PAGE_SIZE), MAX_ROWS) })
      .then(data => {
        if (!cancelled) setReport(withRereads({ ...data, linkState }));
      })
      .catch(err => {
        if (!cancelled) setError(err || 'Please try again.');
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, Opportunity_id, linkState, refreshKey]);

  const refresh = () => setRefreshKey(key => key + 1);

  // A page read's rows can be older than a link re-read that lands while it runs, so those re-reads are laid back over the page.
  const beginPageRead = () => {
    const rereadCount = linkRereadsRef.current.length;
    return page => linkRereadsRef.current.slice(rereadCount).reduce((next, reread) => replaceOpportunityLink(next, reread.hashedUrlStem, reread.link), page);
  };

  const readPage = async limit => {
    const withRereads = beginPageRead();
    const data = await getOpportunityLinks(Opportunity_id, { state: linkState, limit });
    setReport(previous => (previous?.linkState === linkState ? withRereads({ ...data, linkState }) : previous));
  };

  // From the top rather than an offset: a link changing state, here or by a teammate or APM, shifts the server's order.
  const loadMore = async () => {
    setIsLoadingMore(true);
    try {
      await readPage(Math.min(rows.length + PAGE_SIZE, MAX_ROWS));
    } catch (err) {
      window.ALERT.error(`Could not load more links: ${err || 'unknown error'}`);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Re-reads one link by its stem, which skips the participant-pin walk a page load runs.
  const reloadLink = async link => {
    try {
      const { rows: [fresh] } = await getOpportunityLinks(Opportunity_id, { hashedUrlStem: link.hashedUrlStem });
      linkRereadsRef.current.push({ hashedUrlStem: link.hashedUrlStem, link: fresh });
      setReport(previous => (previous?.opportunity.id === Opportunity_id ? replaceOpportunityLink(previous, link.hashedUrlStem, fresh) : previous));
    } catch (err) {
      window.ALERT.error(`Could not reload the link: ${err || 'unknown error'}`);
    }
  };

  // Actions. The link is re-read after every attempt: a teammate may have finished it first.
  const runAction = async (link, work, { refreshPage = false } = {}) => {
    if (busyStems.has(link.hashedUrlStem)) return;
    setBusyStems(stems => new Set(stems).add(link.hashedUrlStem));
    try {
      await work();
      setPickerLink(null);
    } catch (err) {
      window.ALERT.error(`${err?.message || err || 'Action failed'}`);
    } finally {
      // The page read goes out first, so the link's re-read lands while it runs and is laid back over it.
      if (refreshPage) {
        readPage(Math.min(Math.max(loadedCountRef.current, PAGE_SIZE), MAX_ROWS)).catch(err => window.ALERT.error(`Could not refresh the page: ${err || 'unknown error'}`));
      }
      await reloadLink(link);
      setBusyStems(stems => {
        const next = new Set(stems);
        next.delete(link.hashedUrlStem);
        return next;
      });
    }
  };

  const saveProduct = (link, product) =>
    getOpportunityLinkCorrectionRoute(link) === 'evaluation'
      ? correctEvaluation(link.evaluation.id, { Product_id: product.id })
      : updateUrlData(link.hashedUrlStem, getOpportunityLinkUpdate({ kind: 'link', product }, link));

  // A pick that reverts APM's apply can reopen other links on this page, so it re-reads the page as well.
  const pickProduct = (link, product) =>
    runAction(
      link,
      async () => {
        await saveProduct(link, product);
        releaseClaim(link);
      },
      { refreshPage: pickRevertsAgentApply(link) }
    );

  const openPicker = async link => {
    setPickerLink(link);
    // The reserve route refuses a matched stem and needs the Match Product scope; the save of a different product takes its own claim.
    if (link.Product_id || !canMatchUrls) return;
    try {
      const { data } = await reserveUrlData(link.hashedUrlStem);
      if (data.success) return;
      window.ALERT.info(data.reason === 'reserved' ? 'Someone else is working on this link right now.' : 'A teammate already finished this link.');
      setPickerLink(current => (current?.hashedUrlStem === link.hashedUrlStem ? null : current));
      if (data.reason !== 'reserved') reloadLink(link);
    } catch (err) {
      // The save takes its own claim, so a failed reservation check only loses the early warning.
      console.warn(`Could not reserve the link: ${err || 'unknown error'}`);
    }
  };

  const closePicker = () => {
    if (pickerLink) releaseClaim(pickerLink);
    setPickerLink(null);
  };

  const confirmDismiss = link =>
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header='Dismiss this link?'
          subheader='The link stays unmatched for every creator who shared it, and APM will not check it again.'
          selectOptions={SKIP_REASON_OPTIONS}
          selectPlaceholder='Why is it being dismissed?'
          hideInput
          submitBtnDisplay='Dismiss link'
          onCancel={onClose}
          onSubmit={(note, reason) => {
            onClose();
            runAction(link, () => updateUrlData(link.hashedUrlStem, getOpportunityLinkUpdate({ kind: 'skip', reason }, link)));
          }}
        />
      )
    });

  const confirmSendToTeam = link =>
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header='Send to the catalog team?'
          subheader='This link leaves APM review and enters the catalog team’s queue.'
          selectOptions={SEND_BACK_REASON_OPTIONS}
          selectPlaceholder='Why does the catalog team need to review this link?'
          placeholder='Anything else the matcher should know? (optional)'
          rows={2}
          submitBtnDisplay='Send to catalog team'
          onCancel={onClose}
          onSubmit={(note, reason) => {
            onClose();
            runAction(link, () => sendEvaluationToHuman(link.evaluation.id, getSendBackBody(reason, note)));
          }}
        />
      )
    });

  const handleAction = (link, key) => {
    if (key === 'use_pick') return runAction(link, () => saveProduct(link, link.evaluation.agentProduct));
    if (key === 'approve') return runAction(link, () => applyEvaluation(link.evaluation.id));
    if (key === 'choose') return openPicker(link);
    if (key === 'dismiss') return confirmDismiss(link);
    if (key === 'send_to_team') return confirmSendToTeam(link);
    return undefined;
  };

  const copyOpenLinks = async () => {
    const urls = getOpportunityLinkUrls(rows.filter(link => OPEN_LINK_STATES.includes(link.state)));
    try {
      await navigator.clipboard.writeText(urls.join('\n'));
      window.ALERT.success(`Copied ${urls.length} open ${urls.length === 1 ? 'link' : 'links'}. Paste them into Catalog Tool → Bulk Link with the product ID.`);
    } catch {
      window.ALERT.error('Could not copy the links.');
    }
  };

  const opportunity = current?.opportunity;
  const tracking = opportunity && getOpportunityWindow(opportunity);
  const counts = current?.links;
  const stateOptions = OPPORTUNITY_LINK_STATE_OPTIONS.map(option => ({
    value: option.value,
    label: counts ? `${option.label} ${formatNumber(option.value === 'all' ? counts.total : counts[getLinkCountKey(option.value)])}` : option.label
  }));
  const openLoaded = rows.filter(link => OPEN_LINK_STATES.includes(link.state)).length;

  return (
    <div className='silent-eval-opportunity-links'>
      <div className='se-toolbar'>
        <button type='button' className='se-btn ghost' onClick={onBack}>
          ← All opportunities
        </button>
        <SegmentedControl label='Link state' options={stateOptions} value={linkState} onChange={value => patchUrl({ linkState: value })} />
        <button type='button' className='se-btn' disabled={!openLoaded} onClick={copyOpenLinks}>
          Copy {openLoaded ? `${formatNumber(openLoaded)} ` : ''}open links
        </button>
        <a className='se-btn' href='/catalog-tool?tab=link' target='_blank' rel='noopener noreferrer'>
          Bulk Link in Catalog Tool ↗
        </a>
        <button type='button' className='se-btn' disabled={isFetching} onClick={refresh}>
          Refresh
        </button>
        <span className={cn('se-refreshing', { on: isFetching })} aria-hidden='true' />
      </div>

      {error && (
        <div className='ol-error' role='alert'>
          Could not {current ? 'refresh' : 'load'} opportunity {Opportunity_id} — {String(error)}
        </div>
      )}
      {opportunity && (
        <header className='ol-heading' aria-busy={isFetching}>
          <div>
            <p className='ol-eyebrow'>Opportunity {opportunity.id}</p>
            <h2>{opportunity.title || `Opportunity ${opportunity.id}`}</h2>
            <p className='ol-facts'>
              <strong>{opportunity.brandName || 'Unknown brand'}</strong>
              <span>{tracking.range}</span>
              <span className={cn('ol-window', tracking.status)}>{tracking.label}</span>
              <span>{formatNumber(opportunity.participantCount)} participants</span>
              <span>{opportunity.acceptedDomains ? `Links on ${opportunity.acceptedDomains.join(', ')}` : 'Links on any site'}</span>
            </p>
          </div>
          {counts && (
            <p className='ol-summary'>
              {formatNumber(counts.total)} links · {formatNumber(counts.agentMatched + counts.personMatched)} matched · {formatNumber(counts.open)} open
            </p>
          )}
        </header>
      )}

      {!current ? (
        !error && (
          <div className='ol-list'>
            {[0, 1, 2].map(index => (
              <LiveMatchRowSkeleton key={index} />
            ))}
          </div>
        )
      ) : !rows.length && !current.hasMore ? (
        <div className='se-empty'>{counts.total ? 'No links in this state.' : 'No participant links on the sites this opportunity counts yet.'}</div>
      ) : (
        <div className='ol-list'>
          {rows.map(link => (
            <OpportunityLinkRow key={link.hashedUrlStem} link={link} isBusy={busyStems.has(link.hashedUrlStem)} canMatchUrls={canMatchUrls} onAction={key => handleAction(link, key)} />
          ))}
          {current.hasMore &&
            (rows.length < MAX_ROWS ? (
              <button type='button' className='se-btn ol-load-more' disabled={isLoadingMore} onClick={loadMore}>
                {isLoadingMore ? 'Loading…' : `Load more (${formatNumber(current.total - rows.length)} left)`}
              </button>
            ) : (
              <p className='ol-load-more ol-row-cap'>{`Showing the first ${formatNumber(rows.length)} of ${formatNumber(current.total)} links.`}</p>
            ))}
        </div>
      )}

      {pickerLink && (
        <LiveMatchProductPicker
          row={pickerRow}
          note={pickerLink.Product_id ? 'Your pick replaces the current match.' : 'Your pick matches this link for every creator who shared it.'}
          onPick={product => pickProduct(pickerLink, product)}
          onClose={closePicker}
          isSaving={busyStems.has(pickerLink.hashedUrlStem)}
        />
      )}
    </div>
  );
};

// A release that fails lapses on its own when the claim runs out.
const releaseClaim = link => releaseUrlData(link.hashedUrlStem).catch(() => {});

SilentEvalOpportunityLinks.propTypes = {
  Opportunity_id: PropTypes.number.isRequired,
  active: PropTypes.bool,
  onBack: PropTypes.func.isRequired
};

export default SilentEvalOpportunityLinks;
